import { cloneElement, ReactElement, ReactNode, useRef, useState } from 'react';

import { Popover, PopoverProps } from '../../popover';
import { getTooltipAnchorOrigin, getTooltipArrowPoints, getTooltipArrowSize, getTooltipTransformOrigin } from '../lib';
import { TooltipChildrenProps, TooltipPosition } from '../model/types';

import * as stylex from '@stylexjs/stylex';
import { isTextNode } from '@shared/libs/is-text-node';
import { styles } from './styles';

export interface TooltipProps extends Omit<Partial<PopoverProps>, 'title' | 'children'> {
  position: TooltipPosition;
  title: ReactNode;
  children: ReactElement<TooltipChildrenProps>;
  delay?: number;
  enterDelay?: number;
  leaveDelay?: number;
  disabled?: boolean;
  isLightTheme?: boolean;
  isDisabledElement?: boolean;
}

export const Tooltip = ({
  position,
  title,
  children,
  delay = 0,
  enterDelay = delay,
  leaveDelay = delay,
  disabled,
  isLightTheme,
  isDisabledElement,
  ...props
}: TooltipProps) => {
  const [open, setOpen] = useState(false);
  const childrenRef = useRef<HTMLElement>(null);
  const enterTimeoutIdRef = useRef<number | null>(null);
  const leaveTimeoutIdRef = useRef<number | null>(null);
  const arrowSize = getTooltipArrowSize(position);
  const arrowPoints = getTooltipArrowPoints(arrowSize.width, arrowSize.height, position);

  const handleOpen = () => {
    if (disabled) {
      return;
    }

    enterTimeoutIdRef.current = window.setTimeout(() => {
      setOpen(true);
    }, enterDelay);

    if (!leaveTimeoutIdRef.current) {
      return;
    }

    window.clearTimeout(leaveTimeoutIdRef.current);
  };

  const handleClose = () => {
    leaveTimeoutIdRef.current = window.setTimeout(() => {
      setOpen(false);
    }, leaveDelay);

    if (!enterTimeoutIdRef.current) {
      return;
    }

    window.clearTimeout(enterTimeoutIdRef.current);
  };

  const renderTitle = () => {
    if (!isTextNode(title)) return title;
    return <div {...stylex.props(styles.title, isDisabledElement && styles.titleDisabled)}>{title}</div>;
  };

  const getClassNamePosition = () => {
    switch (position) {
      case 'top':
        return styles.popoverTop;
      case 'bottom':
        return styles.popoverBottom;
      case 'left':
        return styles.popoverLeft;
      case 'right':
        return styles.popoverRight;
      case 'topLeft':
        return styles.popoverTopLeft;
      case 'topRight':
        return styles.popoverTopRight;
      case 'bottomLeft':
        return styles.popoverBottomLeft;
      case 'bottomRight':
        return styles.popoverBottomRight;
    }
  };

  const positionStyle = getClassNamePosition();
  const disabledPositionStyle = isDisabledElement && position === 'top' ? styles.popoverTopDisabled : null;

  const popoverClassName = [
    props.className,
    stylex.props(styles.popover, isLightTheme && styles.popoverLight, positionStyle, disabledPositionStyle).className,
  ].join(' ');

  return (
    <>
      {cloneElement(children, {
        ref: childrenRef,
        onPointerEnter: handleOpen,
        onPointerLeave: handleClose,
      })}

      <Popover
        anchorRef={children.props?.ref ?? childrenRef}
        anchorOrigin={getTooltipAnchorOrigin(position)}
        transformOrigin={getTooltipTransformOrigin(position)}
        open={open}
        onClose={handleClose}
        preventOverflow
        onPointerEnter={handleOpen}
        onPointerLeave={handleClose}
        {...props}
        className={popoverClassName}
      >
        <div {...stylex.props(styles.message, isDisabledElement && styles.messageDisabled)}>
          {renderTitle()}
          <div {...stylex.props(styles.shadow)} />
        </div>

        <svg {...arrowSize} {...stylex.props(styles.arrow, getClassNamePosition())}>
          <polygon points={arrowPoints} />
        </svg>
      </Popover>
    </>
  );
};
